import useUpdateEquipmentUsage from "@/common/hooks/queries/useUpdateEquipmentUsage"

import { zodResolver } from "@hookform/resolvers/zod"
import TextArea from "@/common/components/TextArea"

import Modal from "@/common/components/Modal"
import { Dispatch, useEffect, useState } from "react"

import { Controller, useForm } from "react-hook-form"
import { editEquipmentUsageSchema as editEquipmentUsageSchema, EditEquipmentUsageValidationSchema } from "./schema"

import { unities } from "@/common/constants/unities.constant"
import { FormTransformers } from "@/common/utils/formTransformers"
import { UnitTextField } from "@/common/components/UnitTextField"
import { FileUploader } from "@/common/components/FileUploader"
import { ErrorMessage } from "@/common/components/ErrorMessage"
import { formatUnit } from "@/common/utils/formatters"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import { useQueryClient } from "@tanstack/react-query"
import ResumeItem from "@/common/components/ResumeItem"

type EditEquipmentUsageModalProps = {
  setIsOpen: Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  equipmentId: string
  initialValue?: number
  unitForConsumption?: string
  categoryId?: string;
  categoryName?: string;
}

const EditEquipmentUsageModal = ({ setIsOpen, isOpen, equipmentId, categoryId, categoryName, initialValue, unitForConsumption }: EditEquipmentUsageModalProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<EditEquipmentUsageValidationSchema>({
    resolver: zodResolver(editEquipmentUsageSchema),
    defaultValues: {
      value: initialValue?.toString(),
      reason: ""
    }
  })

  const file = watch('file');

  const {
    mutate: updateEquipmentUsage,
    isPending: isUpdateEquipmentUsagePending,
  } = useUpdateEquipmentUsage({
    equipmentId,
    categoryId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipment', equipmentId] })
      queryClient.invalidateQueries({ queryKey: ['clientEquipmentLastUsageDeclarationsPerCategory', equipmentId] })

      setIsOpen(false)
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }) as (error: Error) => void,
  })

  const onSubmit = (formData: EditEquipmentUsageValidationSchema) => {
    updateEquipmentUsage(formData)
  }

  useEffect(reset, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Justificativa de ajuste"
        titleClass="text-[24px]"
        footer="Confirmar justificativa e salvar alteração"
        subtitle="Preencha o motivo para alteração."
        subtitleClassName="mt-4"
        setIsOpen={setIsOpen}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={isUpdateEquipmentUsagePending}
        disabled={isUpdateEquipmentUsagePending}
        dataTestid="submit_create_last_usage_declaration_button"
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-6">
            {categoryId && categoryName && (
              <ResumeItem
                label="Categoria"
                value={categoryName}
              />
            )}

            <Controller
              control={control}
              name="value"
              disabled={isUpdateEquipmentUsagePending}
              render={({ field }) => (
                <UnitTextField
                  markAsRequired
                  className="text-white-800"
                  label="Novo Valor"
                  maxValue={99_999_999}
                  decimals={0}
                  unit={unities[unitForConsumption || 'kilometers']}
                  defaultValue={formatUnit(Number(initialValue) || 0, unitForConsumption || 'kilometers')}
                  errorMessage={errors.value?.message}
                  {...field}
                  dataTestid="create_last_usage_declaration_value_input"
                />
              )}
            />

            <TextArea
              className="text-white-800"
              label="Justificativa"
              markAsRequired
              placeholder="Escreva aqui sua justificativa"
              errorMessage={errors.reason?.message}
              {...register("reason")}
              disabled={isUpdateEquipmentUsagePending}
              dataTestid="create_last_usage_declaration_reason_input"
            />

            <div>
              <FileUploader
                name="file"
                accept="application/pdf, image/png, image/jpg, image/jpeg"
                title="Anexe o documento"
                className="w-full bg-yellow-op-2 border-none text-white-400"
                fileName={file ? file.name : undefined}
                currentFileUrl={file ? URL.createObjectURL(file) : undefined}
                handleChange={(file) => {
                  clearErrors('file')
                  setValue('file', file)
                }}
                setError={(message) => setError('file', { message })}
                disabled={isUpdateEquipmentUsagePending}
                dataTestid="create_last_usage_declaration_file_input"
              />

              {errors.file?.message && (
                <ErrorMessage
                  name="file"
                  message={errors.file?.message}
                />
              )}
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  )
}

export default EditEquipmentUsageModal
